import { useEffect, useRef } from 'react';
import ApiService, { ICountryData } from '../services/api';
import { cookieHelpers } from '../utils/cookieUtils';

// Country to language mapping
const defaultLanguage: Record<string, string> = {
  "Albania": "Albanian",
  "Austria": "German",
  "Belgium": "Dutch",
  "Bosnia-Herzegovina": "Bosnian",
  "Botswana": "English",
  "Bulgaria": "Bulgarian",
  "Croatia": "Croatian",
  "Cyprus": "Greek",
  "Czech Republic": "Czech",
  "Denmark": "Danish",
  "Estonia": "Estonian",
  "Finland": "Finnish",
  "France": "French",
  "French Polynesia": "French",
  "Germany": "German",
  "Ghana": "English",
  "Greece": "Greek",
  "Hungary": "Hungarian",
  "Iceland": "Icelandic",
  "Ireland": "English",
  "Israel": "Hebrew",
  "Italy": "Italian",
  "Kosovo": "Albanian",
  "Latvia": "Latvian",
  "Lesotho": "English",
  "Lithuania": "Lithuanian",
  "Macedonia": "Macedonian",
  "Malta": "English",
  "Moldova": "Romanian",
  "Montenegro": "Montenegrin",
  "Brazil": "Portuguese",
  "Brasil": "Portuguese",
  // LATAM (herba_sa market)
  "Aruba": "Spanish",
  "Argentina": "Spanish",
  "Bolivia": "Spanish",
  "Chile": "Spanish",
  "Colombia": "Spanish",
  "Costa Rica": "Spanish",
  "Ecuador": "Spanish",
  "Guatemala": "Spanish",
  "El Salvador": "Spanish",
  "Honduras": "Spanish",
  "Nicaragua": "Spanish",
  "Panama": "Spanish",
  "Panamá": "Spanish",
  "Paraguay": "Spanish",
  "Peru": "Spanish",
  "Perú": "Spanish",
  "Dominican Republic": "Spanish",
  "República Dominicana": "Spanish",
  "Uruguay": "Spanish",
  "Venezuela": "Spanish",
  "Namibia": "English",
  "Netherlands": "Dutch",
  "North Macedonia": "Macedonian",
  "Norway": "Norwegian",
  "Poland": "Polish",
  "Portugal": "Portuguese",
  "Romania": "Romanian",
  "Serbia": "Serbian",
  "Slovak Republic": "Slovak",
  "Slovakia": "Slovak",
  "Slovenia": "Slovene",
  "South Africa": "English",
  "Spain": "Spanish",
  "Swaziland": "English",
  "Eswatini": "English",
  "Sweden": "Swedish",
  "Switzerland": "German",
  "Turkey": "Turkish",
  "Ukraine": "Ukrainian",
  "United Kingdom": "English",
};

interface UseIpLanguageDetectionProps {
  languages: ICountryData[];
  loading: boolean;
  flowType: 'distributor' | 'customer';
  onLanguageDetected: (languageCode: string) => Promise<void>;
  onLanguageSet: (languageCode: string) => void;
}

export const useIpLanguageDetection = ({
  languages,
  loading,
  flowType,
  onLanguageDetected,
  onLanguageSet,
}: UseIpLanguageDetectionProps) => {
  const ipDetectionAttempted = useRef<boolean>(false);
  const apiService = useRef(new ApiService());

  useEffect(() => {
    // Early return if already attempted
    if (ipDetectionAttempted.current) {
      return;
    }

    const savedLanguage = cookieHelpers.getLanguage(flowType);

    // Treat "es" as no saved language to allow IP detection
    if (savedLanguage && savedLanguage !== 'es') {
      return;
    }

    if (savedLanguage === 'es') {
      cookieHelpers.setLanguage('', flowType);
    }

    if (languages.length === 0) {
      return;
    }

    if (loading) {
      return;
    }

    if ((!savedLanguage || savedLanguage === 'es') && languages.length > 0 && !loading) {
      ipDetectionAttempted.current = true;
      
      // Capture current values to avoid stale closures
      const currentLanguages = languages;
      
      const detectLanguageFromIp = async () => {
        try {
          // Add timeout to prevent long waits
          const timeoutPromise = new Promise<null>((resolve) => {
            setTimeout(() => resolve(null), 3000); // 3 second timeout
          });

          const countryNamePromise = apiService.current.getCountryNameFromIp();
          const countryName = await Promise.race([countryNamePromise, timeoutPromise]);
          
          if (!countryName) {
            // Fallback to Spanish on timeout
            const englishLanguage = currentLanguages.find(
              (lang) => lang.country_language?.toLowerCase() === 'spanish' || lang.country_language_code === 'es'
            );
            
            if (englishLanguage && englishLanguage.country_language_code) {
              onLanguageSet(englishLanguage.country_language_code);
              await onLanguageDetected(englishLanguage.country_language_code);
            } else {
              onLanguageSet('es');
              await onLanguageDetected('es');
            }
            return;
          }
          
          
          if (countryName) {
            // Normalize country name - handle variations
            let normalizedCountryName = countryName.trim();
            
            // Remove "The" prefix if present (e.g., "The Netherlands" -> "Netherlands")
            if (normalizedCountryName.toLowerCase().startsWith('the ')) {
              normalizedCountryName = normalizedCountryName.substring(4).trim();
            }
            
            
            // Try exact match first
            let targetLanguage = defaultLanguage[normalizedCountryName];
            
            // If no exact match, try common variations
            if (!targetLanguage) {
              const countryVariations: Record<string, string> = {
                'UK': 'United Kingdom',
                'Great Britain': 'United Kingdom',
                'GB': 'United Kingdom',
                'The Netherlands': 'Netherlands',
                'The United Kingdom': 'United Kingdom',
                'Peru': 'Perú',
                'Panama': 'Panamá',
                'Dominican Republic': 'República Dominicana',
                'The Dominican Republic': 'República Dominicana',
              };
              
              const mappedCountry = countryVariations[countryName.trim()] || countryVariations[normalizedCountryName];
              if (mappedCountry) {
                targetLanguage = defaultLanguage[mappedCountry];
              }
            }
            
            if (targetLanguage) {
              // Find matching language in the languages array - try multiple matching strategies
              let matchedLanguage = currentLanguages.find(
                (lang) => lang.country_language?.toLowerCase() === targetLanguage.toLowerCase()
              );

              
              if (!matchedLanguage && targetLanguage.toLowerCase() === 'english') {
                matchedLanguage = currentLanguages.find(
                  (lang) => lang.country_language_code?.toLowerCase() === 'en'
                );
              }
              if (!matchedLanguage && targetLanguage.toLowerCase() === 'spanish') {
                matchedLanguage = currentLanguages.find(
                  (lang) => lang.country_language_code?.toLowerCase() === 'es'
                );
              }
              if (!matchedLanguage && targetLanguage.toLowerCase() === 'portuguese') {
                matchedLanguage = currentLanguages.find(
                  (lang) => lang.country_language_code?.toLowerCase() === 'pt'
                );
              }
              
              if (matchedLanguage && matchedLanguage.country_language_code) {
                onLanguageSet(matchedLanguage.country_language_code);
                await onLanguageDetected(matchedLanguage.country_language_code);
                return;
              } 
            } 
          }
          
          // Only fallback to English if IP detection completely fails (no country detected or error)
          // If country was detected but language not found, don't set any language
          if (!countryName) {
            // No country detected - fallback to Spanish
            const englishLanguage = currentLanguages.find(
              (lang) => lang.country_language?.toLowerCase() === 'spanish' || lang.country_language_code === 'es'
            );
            
            if (englishLanguage && englishLanguage.country_language_code) {
              onLanguageSet(englishLanguage.country_language_code);
              await onLanguageDetected(englishLanguage.country_language_code);
            } else {
              onLanguageSet('es');
              await onLanguageDetected('es');
            }
          }
        } catch (error) {
          console.error('[useIpLanguageDetection] Error detecting language from IP:', error);
          // Fallback to Spanish only on error
          const englishLanguage = currentLanguages.find(
            (lang) => lang.country_language?.toLowerCase() === 'spanish' || lang.country_language_code === 'es'
          );
          
          if (englishLanguage && englishLanguage.country_language_code) {
            onLanguageSet(englishLanguage.country_language_code);
            await onLanguageDetected(englishLanguage.country_language_code);
          } else {
            onLanguageSet('es');
            await onLanguageDetected('es');
          }
        }
      };
      
      detectLanguageFromIp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languages.length, loading]);
};

